import { useContext, useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import State from "../../../../../../context";
import dayjs from "dayjs";
import { theme } from "antd";
import CardSkeleton from "../../../../../../atom/CardSkeleton";
import ModalAdvancedSettings from "../ModalAdvancedSettings";
import ModalConfirmSaveProposal from "../ModalConfirmSaveProposal";
import ModalGoalSettings from "../../../../../goals/components/ModalGoalSettings";
import ModalLevelProductDetails from "../../../admin/component/ModalLevelProductDetails";
import ModalLevelRelatedProducts from "../ModalLevelRelatedProducts";
import ModalOptionsInvestmentCard from "../ModalOptionsInvestmentCard";
import SectionGoalDetails from "./component/SectionGoalDetails";
import SectionReviewProposal from "./component/SectionReviewProposal";

import StyledContainer from "./style";

import {
  DEFAULT_ADVISOR_FEE,
  DEFAULT_BIRTH_YEAR,
  DEFAULT_RETIREMENT_AGE,
  DEFAULT_WITHDRAWAL_START_YEAR,
  DEFAULT_WITHDRAWAL_LEVEL,
} from "../../../../../../utils/constant";
import {
  PROPOSAL_DEFAULT_DATA_LEVEL,
  DEFAULT_SIMULATIONS_AMOUNT,
} from "../../constant";

import {
  convertCurrentAgeRange,
  convertInvestmentDurationRange,
  getLastSavedProposalProps,
  getManuallyUpdatedOAGoalRecommendedProduct,
  isGrowthTypeRiskTolerance,
  isLockedByAdminView,
} from "../../../../../../utils/helper/specialized";
import { getValidProductId } from "../../helper";
import {
  addGoal,
  getLiveAssessmentV3,
  getLiveSimulationV3,
  getLiveStressorV3Chance,
  getLiveStressorV3Projection,
  getMc,
  updateGoal,
} from "../../request";
import { getPercentValue } from "../../../../../../utils/helper/general";

const ViewOneGoal = ({ handleDownloadProposal }) => {
  const { token } = theme.useToken();
  const [state] = useContext(State);

  const [activeStressTestData, setActiveStressTestData] = useState(null);
  const [activeView, setActiveView] = useState("projection");
  const [proposalData, setProposalData] = useState(null);

  const [productsLiveAssessment, setProductsLiveAssessment] = useState([]);
  const [productsLiveSimulation, setProductsLiveSimulation] = useState([]);
  const [stressorChance, setStressorChance] = useState(null);
  const [stressorProjection, setStressorProjection] = useState(null);
  const [mcData, setMcData] = useState(null);

  const [chartLoading, setChartLoading] = useState(true);
  const [isCompareChartDirty, setIsCompareChartDirty] = useState(false);

  const productMap = state.getPreferenceValue("productMap");
  const guideInitialData =
    state.getPreferenceValue("leadInitial") ??
    state.getPreferenceValue("guideInitialData");

  const selectedProductData = state.productsList?.find(
    it => it._id === state.selectedProduct
  );

  const birthYear =
    proposalData?.birthYear ??
    (proposalData?.currentAge
      ? dayjs().format("YYYY") - proposalData.currentAge
      : DEFAULT_BIRTH_YEAR);

  // initialize active proposal data
  useEffect(() => {
    if (!state.preferences || proposalData || state.loading) return;

    let proposalInitialState = {};

    const prospectObjective = state.getPreferenceValue("prospectObjective");

    const valueAdvisorFeeFromAdmin = state.settings?.financialFactors?.enabled
      ? getPercentValue(state.settings.financialFactors.data.globalAdvisorFee)
      : undefined;

    if (state.activeGoal && productMap) {
      const activeGoalLastSavedProps = productMap[state.activeGoal].at(-1);

      proposalInitialState = {
        ...activeGoalLastSavedProps,
        advisorFee:
          valueAdvisorFeeFromAdmin ?? activeGoalLastSavedProps.advisorFee,
        birthYear:
          guideInitialData?.yearBorn || activeGoalLastSavedProps.birthYear,
        contributions:
          activeGoalLastSavedProps.contributions ??
          activeGoalLastSavedProps.savingsEachMonth * 12,
        lengthOfInvestment: undefined,
        retirementAge:
          guideInitialData?.retirementAge ?? DEFAULT_RETIREMENT_AGE,
      };
    } else if (productMap) {
      const lastSavedProposalProps = getLastSavedProposalProps(productMap);
      const goalInitialData = prospectObjective ?? guideInitialData;

      proposalInitialState = {
        ...goalInitialData,
        ...lastSavedProposalProps,
        advisorFee:
          valueAdvisorFeeFromAdmin ?? lastSavedProposalProps.advisorFee,
        birthYear:
          guideInitialData?.yearBorn ||
          goalInitialData?.birthYear ||
          DEFAULT_BIRTH_YEAR,
        lengthOfInvestment: undefined,
        retirementAge: goalInitialData?.retirementAge ?? DEFAULT_RETIREMENT_AGE,
        timestamp: undefined,
      };
    } else if (prospectObjective) {
      const investmentDuration = isNaN(prospectObjective.investmentDuration)
        ? convertInvestmentDurationRange(prospectObjective.investmentDuration)
        : +prospectObjective.investmentDuration;

      const currentAge =
        prospectObjective.currentAge ??
        convertCurrentAgeRange(prospectObjective.currentAgeRange);

      proposalInitialState = {
        ...prospectObjective,
        advisorFee:
          valueAdvisorFeeFromAdmin ??
          prospectObjective.advisorFee ??
          DEFAULT_ADVISOR_FEE,
        birthYear:
          guideInitialData?.yearBorn ||
          prospectObjective.birthYear ||
          DEFAULT_BIRTH_YEAR,
        currentAge,
        incomeChartType: isGrowthTypeRiskTolerance(
          prospectObjective.investmentObjective
        )
          ? "contributions"
          : "income",
        investmentDuration,
        lengthOfInvestment: undefined,
        retirementAge:
          guideInitialData?.retirementAge ?? DEFAULT_RETIREMENT_AGE,
        withdrawalLevel:
          prospectObjective.withdrawalLevel ?? DEFAULT_WITHDRAWAL_LEVEL,
        yearToStartWithdrawals:
          prospectObjective.yearToStartWithdrawals ||
          Math.round(investmentDuration / 2) ||
          DEFAULT_WITHDRAWAL_START_YEAR,
      };
    } else {
      // onboarding quiz skipped, set default values
      proposalInitialState = {
        ...PROPOSAL_DEFAULT_DATA_LEVEL,
        advisorFee:
          valueAdvisorFeeFromAdmin ?? PROPOSAL_DEFAULT_DATA_LEVEL.advisorFee,
        timestamp: guideInitialData?.startDate
          ? new Date(guideInitialData?.startDate).getTime()
          : undefined,
      };
    }

    const validProductId = getValidProductId({
      organization: state.organization?.name,
      productsList: state.productsList,
      proposalProductId:
        proposalInitialState.productId ??
        proposalInitialState.recommendedModelId,
      proposalRiskTolerance: proposalInitialState.riskTolerance?.toLowerCase(),
    });

    console.log({
      productIdInDB: proposalInitialState.productId,
      validProductId,
    });

    setProposalData({
      ...proposalInitialState,
      productId: validProductId,
    });

    state.setKeyValue("selectedProduct", validProductId);
    state.setKeyValue("compareProducts", [validProductId]);
  }, [state]);

  // fetch projection calculation, when proposal data changed
  useEffect(() => {
    if (!selectedProductData || !proposalData) return;

    if (
      !isGrowthTypeRiskTolerance(proposalData?.investmentObjective) &&
      proposalData?.investmentDuration <= proposalData?.yearToStartWithdrawals
    ) {
      state.showWarning(
        "Length of investment must be greater than the begin withdrawals year"
      );
      return;
    }

    getProjectionCalculation();

    if (state.showMonteCarlo) {
      getMcSimulation();
    }
  }, [selectedProductData, proposalData]);

  // fetch stressor calculation
  useEffect(() => {
    if (!activeStressTestData) {
      setStressorProjection(null);
      setStressorChance(null);
      return;
    }

    getStressorSimulation();
  }, [activeStressTestData, proposalData]);

  // fetch projection calculation in comparison mode
  useEffect(() => {
    if (!isCompareChartDirty) return;

    getProjectionCalculation();
  }, [isCompareChartDirty]);

  const getProjectionCalculation = async () => {
    setChartLoading(true);

    const organization =
      (state.organization?.name?.toLowerCase() ??
        state.orgName?.toLowerCase()) === "level"
        ? "level2"
        : (state.organization?.name ?? state.orgName);

    const responsesAssessment = [];
    const responsesSimulation = [];

    const assessmentPromises = state.compareProducts.map((productId, index) => {
      const requestBody = {
        ...proposalData,
        investmentDuration:
          proposalData.investmentDuration ?? proposalData.lengthOfInvestment,
        organization,
        productId,
      };

      return getLiveAssessmentV3(requestBody)
        .then(data => {
          responsesAssessment.push({
            productId,
            ...data,
            color: token.comparison_products_colors[index],
            shape: productId === selectedProductData._id ? "Line" : "Dash",
            isMainProduct: productId === selectedProductData._id,
          });

          if (index === 0) {
            state.setKeyValue("productLiveAssessment", data);

            const insufficientFundsData = data?.mainProjection
              ?.filter(it =>
                it.Date.includes(data.mainProjection[0].Date.slice(4, 10))
              )
              ?.find(it => it.Date !== null && it.Withdrawals);

            if (insufficientFundsData) {
              state.setKeyValue(
                "insufficientFundsStartingIn",
                birthYear + insufficientFundsData[0]
              );
            } else {
              state.setKeyValue("insufficientFundsStartingIn", null);
            }
          }
        })
        .catch(error => state.showError(error));
    });

    const simulationPromises = state.compareProducts.map((productId, index) => {
      const requestBody = {
        ...proposalData,
        organization,
        productId,
        investmentDuration:
          proposalData.investmentDuration ?? proposalData.lengthOfInvestment,
        inputSettings: {},
        montecarlo: true,
        historicalAnalysis: true,
        simulations: DEFAULT_SIMULATIONS_AMOUNT,
      };

      return getLiveSimulationV3(requestBody)
        .then(data => {
          if (index === 0) {
            // calculation for selected financial product
            // always at the first spot in the array

            // global value required by regression testing scripts
            window.lastQuickSim = data?.PoSEndofHorizon;

            state.setKeyValue("productsLiveSimulation", data);
          }

          responsesSimulation.push({
            productId,
            ...data,
            color: token.comparison_products_colors[index],
            isComparedProduct: productId !== selectedProductData._id,
          });
        })
        .catch(error => state.showError(error));
    });

    await Promise.all(assessmentPromises);
    await Promise.all(simulationPromises);

    setProductsLiveAssessment(responsesAssessment);
    setProductsLiveSimulation(responsesSimulation);
    setChartLoading(false);
    setIsCompareChartDirty(false);
  };

  const getStressorSimulation = async () => {
    const eventData = {
      ...activeStressTestData,
      key: activeStressTestData.key ?? "custom",
    };

    const requestBody = {
      marketEvent: [eventData],
      ...proposalData,
      productId: state.selectedProduct ?? proposalData.productId,
      investmentDuration: +proposalData.investmentDuration,
      withdrawalLevel: proposalData.withdrawalLevel ?? DEFAULT_WITHDRAWAL_LEVEL,
    };

    try {
      const projection = await getLiveStressorV3Projection(requestBody);

      setStressorProjection(projection);

      const chance = await getLiveStressorV3Chance(requestBody);

      setStressorChance(chance);
    } catch (error) {
      console.error("Error fetching stress test calculations:", error);
    }

    setChartLoading(false);
  };

  const getMcSimulation = () => {
    setChartLoading(true);

    const requestBody = {
      ...proposalData,
      productId: state.selectedProduct ?? proposalData.productId,
      investmentDuration: +proposalData.investmentDuration,
      withdrawalLevel: proposalData.withdrawalLevel ?? DEFAULT_WITHDRAWAL_LEVEL,
    };

    getMc(requestBody)
      .then(data => {
        setMcData(data);
        setActiveView("projection");
      })
      .catch(state.showError)
      .finally(() => {
        setChartLoading(false);
      });
  };

  const handleProposalDataChange = useDebouncedCallback((values, skipDirty) => {
    if (!isLockedByAdminView(state.showWarning)) {
      !skipDirty && state.setKeyValue("isProposalDirty", true);

      setProposalData(lastState => ({
        ...lastState,
        ...values,
      }));
    }
  }, 1000);

  const handleCardClick = cardId => {
    state.setKeyValue("productDetailsId", cardId);
    state.setKeyValue("openModalProductDetails", true);
  };

  const handleSaveProposal = () => {
    state.setKeyValue("showOverlay", true);

    const request = state.activeGoal ? updateGoal : addGoal;

    state.setKeyValue("openModalConfirmSaveProposal", false);

    request({
      email: state._id,
      goalId: state.activeGoal,
      proposalData: {
        ...proposalData,
        calculated: {
          endingInvestmentBalance: productsLiveAssessment
            .find(it => it.isMainProduct)
            ?.mainProjection?.at(-1)?.Value,
        },
        investmentDuration:
          proposalData?.investmentDuration ?? proposalData?.lengthOfInvestment,
        organization: state.organization?.name?.toLowerCase(),
        productId: state.selectedProduct,
      },
    })
      .then(() => {
        state.setKeyValue("isProposalDirty", false);

        state
          .setUserData()
          .then(data => {
            state.showSuccess("Proposal saved");

            if (state.activeGoal) {
              //update existed active goal
              if (data.preferences?.valueMap?.productMap) {
                setProposalData(
                  data.preferences.valueMap.productMap[state.activeGoal].at(-1)
                );
              }
            } else {
              //set new created goal ID as active goal
              const createdGoalId = Object.keys(
                data.preferences.valueMap.productMap
              ).at(-1);

              state.setKeyValue("activeGoal", createdGoalId);
              setProposalData(
                data.preferences.valueMap.productMap[createdGoalId].at(-1)
              );
            }
          })
          .catch(state.showError)
          .finally(() => state.setKeyValue("showOverlay", false));
      })
      .catch(error => {
        state.showError(error);
        state.setKeyValue("showOverlay", false);
      });
  };

  const handleUpdateGoal = updatedProposalData => {
    setProposalData(updatedProposalData);
    state.setKeyValue("goalSettingsModal", false);
    state.showSuccess("Goal settings updated");
    state.setKeyValue("isProposalDirty", true);

    const recommendedProduct = getManuallyUpdatedOAGoalRecommendedProduct({
      customRiskTolerance: updatedProposalData.riskTolerance,
      currentSelectedProduct: state.productsList.find(
        it => it._id === state.selectedProduct
      ),
      productsList: state.productsList,
    });

    if (recommendedProduct) {
      state.setKeyValue("selectedProduct", recommendedProduct._id);
      state.setKeyValue("compareProducts", [recommendedProduct._id]);
    }
  };

  const handleSelectFinancialProduct = productData => {
    if (!productData) return;

    state.setKeyValue("openModalRelatedProducts", false);

    if (productData.riskTolerance) {
      // not all the financial products have riskTolerance
      handleProposalDataChange({
        ...proposalData,
        riskTolerance: productData.riskTolerance,
      });
    }

    state.setKeyValue("selectedProduct", productData._id);
    state.setKeyValue("compareProducts", [productData._id]);
    state.setKeyValue("isProposalDirty", true);
  };

  const handleAddToCompare = productData => {
    if (!productData) return;

    if (state.compareProducts?.includes(productData._id)) {
      state.showWarning("Product already added to the Compare.");
      return;
    }

    state.setKeyValue("openModalRelatedProducts", false);
    state.setKeyValue("compareProducts", [
      ...state.compareProducts,
      productData._id,
    ]);

    setIsCompareChartDirty(true);
  };

  const handleButtonProductOptions = activeProductId => {
    state.setKeyValue("activeProductId", activeProductId);
    state.setKeyValue("openModalOptionsInvestmentCard", true);
  };

  const handleCloseCompareMode = () => {
    state.setKeyValue("compareProducts", [selectedProductData?._id]);
    setIsCompareChartDirty(true);
    state.setKeyValue("showCompareMode", false);
  };

  const handleCloseMonteCarlo = () => {
    state.setKeyValue("showMonteCarlo", false);
    setMcData(null);
  };

  const handleCloseStressTesting = () => {
    state.setKeyValue("showStressTesting", false);
    setActiveStressTestData(null);
  };

  const handleInvestmentAssumptionsChange = newValues => {
    handleProposalDataChange(newValues);

    if (newValues.investmentObjective === "growth" && activeView === "income") {
      setChartLoading(true);
      setActiveView("projection");
    }
  };

  const handleRemoveFromThisSection = () => {
    state.setKeyValue(
      "compareProducts",
      state.compareProducts.filter(id => id !== state.activeProductId)
    );
    setIsCompareChartDirty(true);
    state.setKeyValue("openModalOptionsInvestmentCard", false);
  };

  const handleSelectOtherProduct = () => {
    state.setKeyValue("openModalOptionsInvestmentCard", false);
    state.setKeyValue("openModalRelatedProducts", true);
  };

  const handleShowCompareMode = () => {
    const selectedProductRiskTolerance = selectedProductData.riskTolerance;
    const firstUnicRiskTolerance =
      selectedProductRiskTolerance === "Moderate" ? "Growth" : "Moderate";
    const secondUnicRiskTolerance =
      selectedProductRiskTolerance === "Aggressive"
        ? "Preservation"
        : "Aggressive";

    state.setKeyValue(
      "compareProducts",
      [
        selectedProductData._id,
        state.productsList.find(
          it => it.riskTolerance === firstUnicRiskTolerance
        )?._id,
        state.productsList.find(
          it => it.riskTolerance === secondUnicRiskTolerance
        )?._id,
      ].filter(it => !!it) // there could be no similar products
    );

    setIsCompareChartDirty(true);
    state.setKeyValue("showCompareMode", true);
  };

  const handleShowMonteCarlo = () => {
    state.setKeyValue("showMonteCarlo", true);
    getMcSimulation();
  };

  const handleShowStressTest = () => {
    state.setKeyValue("showStressTesting", true);
  };

  const submitAdvancedSettings = newValues => {
    setProposalData(lastState => ({
      ...lastState,
      ...newValues,
    }));
    state.setKeyValue("openModalAdvancedSettings", false);
    state.setKeyValue("isProposalDirty", true);
  };

  return (
    <StyledContainer token={token}>
      <CardSkeleton loading={state.loading}>
        {state.activeSection === "reviewProposal" ? (
          <SectionReviewProposal
            handleClose={() =>
              state.setKeyValue("activeSection", "goalDetails")
            }
            handleDownloadProposal={handleDownloadProposal}
            productLiveAssessment={state.productLiveAssessment}
            productsLiveSimulation={state.productsLiveSimulation}
            proposalData={proposalData}
            selectedProductData={state.productsList?.find(
              it => it._id === state.selectedProduct
            )}
            proposalId={state.activeGoal}
          />
        ) : (
          <SectionGoalDetails
            accentHeroSection={
              state.showMonteCarlo ||
              state.showStressTesting ||
              state.showCompareMode
            }
            activeStressTestData={activeStressTestData}
            activeView={activeView}
            chartLoading={chartLoading}
            getMcSimulation={getMcSimulation}
            handleButtonProductOptions={handleButtonProductOptions}
            handleCardClick={handleCardClick}
            handleCloseCompareMode={handleCloseCompareMode}
            handleCloseMonteCarlo={handleCloseMonteCarlo}
            handleCloseStressTesting={handleCloseStressTesting}
            handleInvestmentAssumptionsChange={
              handleInvestmentAssumptionsChange
            }
            handleShowCompareMode={handleShowCompareMode}
            handleShowMonteCarlo={handleShowMonteCarlo}
            handleShowStressTest={handleShowStressTest}
            mcData={mcData}
            productsLiveAssessment={productsLiveAssessment}
            productsLiveSimulation={productsLiveSimulation}
            proposalData={proposalData}
            setActiveStressTestData={setActiveStressTestData}
            setActiveView={setActiveView}
            setChartLoading={setChartLoading}
            stressorChance={stressorChance}
            stressorProjection={stressorProjection}
          />
        )}
      </CardSkeleton>

      <ModalAdvancedSettings
        disableFieldAdvisorFee={
          state.settings?.financialFactors?.enabled &&
          !state.settings?.financialFactors?.data?.allowOverrideAdvisorFee
        }
        handleClose={() =>
          state.setKeyValue("openModalAdvancedSettings", false)
        }
        loading={state.loading}
        onFinish={submitAdvancedSettings}
        open={state.openModalAdvancedSettings}
        proposalData={proposalData}
      />
      <ModalConfirmSaveProposal
        handleClose={() =>
          state.setKeyValue("openModalConfirmSaveProposal", false)
        }
        handleSaveProposal={handleSaveProposal}
        isFirstSave={!(productMap ?? [])[state.activeGoal]}
        open={state.openModalConfirmSaveProposal}
      />
      <ModalGoalSettings
        handleClose={() => state.setKeyValue("goalSettingsModal", false)}
        handleUpdateGoal={handleUpdateGoal}
        loading={state.loading}
        open={state.goalSettingsModal}
        organization={state.organization?.name}
        proposalData={proposalData}
      />
      <ModalLevelProductDetails
        handleClose={() => state.setKeyValue("openModalProductDetails", false)}
        investmentAssumptions={proposalData}
        open={state.openModalProductDetails}
        product={state.productsList?.find(
          it => it._id === state.productDetailsId
        )}
        showError={state.showError}
      />
      <ModalLevelRelatedProducts
        handleActionClick={
          state.showCompareMode
            ? handleAddToCompare
            : handleSelectFinancialProduct
        }
        handleClose={() => state.setKeyValue("openModalRelatedProducts", false)}
        listProductsActive={[state.selectedProduct]}
        open={state.openModalRelatedProducts}
        proposalData={proposalData}
      />
      <ModalOptionsInvestmentCard
        handleSelectOtherProduct={handleSelectOtherProduct}
        handleRemoveFromThisSection={handleRemoveFromThisSection}
        onCancel={() =>
          state.setKeyValue("openModalOptionsInvestmentCard", false)
        }
        open={state.openModalOptionsInvestmentCard}
        showButtonSelectOtherProduct={!state.showCompareMode}
        showButtonRemoveFromThisSection={
          state.showCompareMode &&
          state.activeProductId !== selectedProductData?._id
        }
      />
    </StyledContainer>
  );
};

export default ViewOneGoal;
